import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import usePermissions from "hooks/usePermissions";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import { FilterConfig } from "types/Common";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";

export default function useQuoteFilters(isV2 = false) {
  const [searchParams] = useSearchParams();
  const status: string = searchParams.get("display_status") || "";
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { getPaymentLocationLabel } = usePermissions();
  const paymentLocations = useSortedPaymentLocations();

  // for client side filtering we don't have payment location id, so we'll filter with string title instead
  const paymentLocationWithTitle = useMemo(
    () =>
      paymentLocations.map((item) => ({
        ...item,
        value: item.label,
      })),
    [paymentLocations],
  );

  const QUOTES_DISPLAY_STATUS = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "quote.listing.openForQuotations", defaultMessage: "Open for quotations" }),
        value: "open_for_quotations",
      },
      {
        label: intl.formatMessage({ id: "quote.listing.closedForQuotation", defaultMessage: "Closed for quotations" }),
        value: "closed_for_quotations",
      },
      {
        label: intl.formatMessage({ id: "tabs.awaitingAction", defaultMessage: "Awaiting action" }),
        value: "awaiting_action",
      },
      {
        label: intl.formatMessage({ id: "tabs.approvalPending", defaultMessage: "Approval pending" }),
        value: "approval_pending",
      },
      // {
      //   label: intl.formatMessage({ id: "general.canceled", defaultMessage: "Canceled" }),
      //   value: "canceled,auto_canceled",
      // },
      // {
      //   label: intl.formatMessage({ id: "general.draft", defaultMessage: "Draft" }),
      //   value: "drafts",
      // },
    ],
    [intl],
  );

  const config: FilterConfig[] = [
    {
      filterType: "multiselect",
      label: intl.formatMessage({ id: "general.displayStatus", defaultMessage: "Display status" }),
      key: "display_status",
      options: QUOTES_DISPLAY_STATUS,
    },
    {
      filterType: "multiselect",
      label: getPaymentLocationLabel(),
      key: "payment_location",
      isSearchable: true,
      options: isV2 && !["draft", "system_draft", "archived"].includes(status) ? paymentLocationWithTitle : paymentLocations,
    },
    {
      filterType: "freight_mode",
      label: intl.formatMessage({ id: "general.freightMode", defaultMessage: "Freight mode" }),
      key: "freight_mode",
      filterValueKey: isV2 && !["draft", "system_draft", "archived"].includes(status) ? "value" : "key",
    },
    {
      filterType: "accordion",
      label: intl.formatMessage({ id: "general.origin", defaultMessage: "Origin" }),
      key: "origin",
      nestedFilters: [
        {
          filterType: "date",
          label: intl.formatMessage({ id: "general.pickupDate", defaultMessage: "Pickup date" }),
          key: "pickup_date",
        },
        {
          filterType: "text",
          label: intl.formatMessage({ id: "general.pickupCity", defaultMessage: "Pickup city" }),
          key: "pickup_city",
          ...(isV2 &&
            !["draft", "system_draft", "archived"].includes(status) && {
              options: [
                ...new Set(
                  queryClient
                    .getQueryData<Array<any>>(quoteRequestQueryKeys.allQuotes)
                    ?.map((item) => item?.pickup_city)
                    ?.filter((item) => item),
                ),
              ],
            }),
        },
      ],
    },
    {
      filterType: "accordion",
      label: intl.formatMessage({ id: "general.destination", defaultMessage: "Destination" }),
      key: "destination",
      nestedFilters: [
        {
          filterType: "date",
          label: intl.formatMessage({ id: "report.DeliveryDate", defaultMessage: "Delivery date" }),
          key: "delivery_date",
        },
        {
          filterType: "text",
          label: intl.formatMessage({ id: "general.deliveryCity", defaultMessage: "Delivery city" }),
          key: "delivery_city",
          ...(isV2 &&
            !["draft", "system_draft", "archived"].includes(status) && {
              options: [
                ...new Set(
                  queryClient
                    .getQueryData<Array<any>>(quoteRequestQueryKeys.allQuotes)
                    ?.map((item) => item?.stop1_city)
                    ?.filter((item) => item),
                ),
              ],
            }),
        },
      ],
    },
    {
      filterType: "text",
      label: intl.formatMessage({ id: "general.createdBy", defaultMessage: "Created by" }),
      key: "created_by",
      ...(isV2 &&
        !["draft", "system_draft", "archived"].includes(status) && {
          options: [
            ...new Set(
              queryClient
                .getQueryData<Array<any>>(quoteRequestQueryKeys.allQuotes)
                ?.map((item) => item?.created_by?.name)
                ?.filter((item) => item),
            ),
          ],
        }),
    },
    {
      filterType: "multiselect",
      label: intl.formatMessage({ id: "general.quotationReceived", defaultMessage: "Quotation received" }),
      key: "no_quotations_received",
      options: [
        {
          label: intl.formatMessage({
            id: "general.quotationReceived",
            defaultMessage: "Quotation received",
          }),
          value: 1,
        },
      ],
    },
  ];

  return { config };
}
